import { useState, useEffect, useCallback } from "react"
import FileCard from "../FileCard"
import FileViewer from "../FileViewer"
import ButtonUpload from "../buttonUpload"
import "../../Css/FileManager.css"
import { db } from "../../firebaseConect";
import { doc, setDoc, arrayUnion, collection, query, where, getDocs } from "firebase/firestore"

export default function FileManager({ mode = "myfiles", userEmail }) {
  const baseURL = process.env.REACT_APP_URLBASE
  const [fileList, setFileList] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [sortBy, setSortBy] = useState("date")
  const [sortOrder, setSortOrder] = useState("desc")
  const [selectedFile, setSelectedFile] = useState(null)
  const [isViewerOpen, setIsViewerOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)


  // Función para obtener archivos según el modo
  const fetchFiles = async () => {
    console.log(`🔍 Iniciando fetchFiles con modo: ${mode}, userEmail: ${userEmail}`)

    try {
      if (mode === "shared") {
        // Obtener archivos compartidos desde Firestore
        if (!userEmail) {
          setError("No se pudo obtener tu email. Inicia sesión nuevamente.")
          setLoading(false)
          return
        }
        console.log("📤 Obteniendo archivos compartidos para:", userEmail);

        const q = query(collection(db, "sharedFile"), where("sharedWith", "array-contains", userEmail))
        const querySnapshot = await getDocs(q)
        const sharedFiles = []
        console.log(`📂 Archivos compartidos encontrados: ${querySnapshot.size}`)

        querySnapshot.forEach((doc) => {
          const data = doc.data()
          sharedFiles.push({
            ...data,
            r2Key: doc.id, // Usar el ID del documento como r2Key
          })
        })

        setFileList(sharedFiles)
      } else {
        // Obtener mis archivos desde el backend
        console.log("📁 Obteniendo mis archivos...")
        const userId = getCookieFromDocument("userId")

        const response = await fetch(`${baseURL}/update/list`, {
          method: "GET",
          headers: {
            "user-id": userId || "",
          },
          credentials: "include",
        })

        if (response.ok) {
          const data = await response.json()
          setFileList(data.files || [])
          console.log("📁 Mis archivos encontrados:", data.files?.length || 0)
        } else {
          throw new Error("Error al obtener archivos del servidor")
        }
      }
    } catch (error) {
      console.error("❌ Error fetching files:", error)
      setError(error.message)
      setFileList([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    console.log("🚨 CAMBIO DE MODO DETECTADO:", mode)
    setLoading(true)
    setFileList([]) // Reset list cuando cambia el modo
    fetchFiles()
    // eslint-disable-next-line
  }, [mode, userEmail])

  const handleShareFile = async (file, email) => {
    try {
      if (!userEmail) {
        alert("No se pudo obtener tu email. Inicia sesión nuevamente.")
        return
      }

      console.log("🔄 Compartiendo archivo:", file.fileName, "con:", email)

      const fileRef = doc(db, "sharedFile", file.r2Key)
      await setDoc(
        fileRef,
        {
          r2Key: file.r2Key,
          sharedWith: arrayUnion(email),
          owner: userEmail,
          fileName: file.fileName,
          uploadedAt: file.uploadedAt,
          size: file.size || null,
        },
        { merge: true },
      )

      console.log("✅ Archivo compartido exitosamente")
      alert(`Archivo "${file.fileName}" compartido con ${email}`)
    } catch (error) {
      console.error("❌ Error al compartir:", error)
      alert("Error al compartir: " + error.message)
    }
  }

  function getCookieFromDocument(name) {
    const value = `; ${document.cookie}`
    const parts = value.split(`; ${name}=`)
    if (parts.length === 2) return parts.pop().split(";").shift()
    return null
  }

  const handleViewFile = (file) => {
    setSelectedFile(file)
    setIsViewerOpen(true)
  }

  const handleDeleteFile = async (file) => {
    try {
      const { auth } = await import("../../firebase")
      const user = auth.currentUser
      if (!user) {
        alert("Debes iniciar sesión para eliminar el archivo")
        return
      }

      const token = await user.getIdToken()
      const userId = getCookieFromDocument("userId")

      const res = await fetch(`${baseURL}/delete/${encodeURIComponent(file.r2Key)}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "user-id": userId || "",
        },
        credentials: "include",
      })

      if (!res.ok) {
        const errorData = await res.json()
        throw new Error(errorData.message || "Error al eliminar el archivo")
      }

      alert("Archivo eliminado correctamente")
      fetchFiles()
    } catch (error) {
      console.error("Error al eliminar archivo:", error)
      alert(error.message || "Error al eliminar el archivo")
    }
  }

  const filteredFiles = fileList
    .filter((file) => file.fileName && file.fileName.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (sortBy === "name") {
        return sortOrder === "asc" ? a.fileName.localeCompare(b.fileName) : b.fileName.localeCompare(a.fileName)
      } else {
        const dateA = new Date(a.uploadedAt || 0).getTime()
        const dateB = new Date(b.uploadedAt || 0).getTime()
        return sortOrder === "asc" ? dateA - dateB : dateB - dateA
      }
    })

  return (
    <div className="file-manager">
      <div className="file-manager-header">
        <h1>{mode === "shared" ? "Archivos Compartidos" : "Tu Nube Privada"}</h1>
        <p>
          {mode === "shared" ? "Archivos que otros han compartido contigo" : "Gestiona tus archivos de forma segura"}
        </p>
        {/* Debug info */}
        <div className="debug-info">
          <small>
            Modo: <strong>{mode}</strong> | Email: <strong>{userEmail || "undefined"}</strong>
          </small>
        </div>
      </div>

      <div className="file-controls">
        <input
          type="text"
          placeholder="Buscar archivos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="file-search"
        />

        <div className="sort-controls">
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="sort-select">
            <option value="date">Fecha</option>
            <option value="name">Nombre</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
            className="sort-order-btn"
            title={`Ordenar ${sortOrder === "asc" ? "descendente" : "ascendente"}`}
          >
            {sortOrder === "asc" ? "↑" : "↓"}
          </button>
          <button onClick={fetchFiles} className="refresh-btn" title="Actualizar">
            🔄
          </button>
        </div>
      </div>

      <div className="file-grid-container">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Cargando archivos...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <div className="error-icon">⚠️</div>
            <h3>Error al cargar archivos</h3>
            <p>{error}</p>
            {mode === "shared" && !userEmail && (
              <p>
                <small>Esperando información de usuario...</small>
              </p>
            )}
            <button onClick={fetchFiles} className="retry-btn">
              Reintentar
            </button>
          </div>
        ) : filteredFiles.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📂</div>
            <h3>
              {searchTerm
                ? "No se encontraron archivos"
                : mode === "shared"
                  ? "No tienes archivos compartidos"
                  : "Aún no tienes archivos"}
            </h3>
            <p>
              {searchTerm
                ? "Intenta con otros términos de búsqueda"
                : mode === "shared"
                  ? "Cuando alguien comparta archivos contigo aparecerán aquí"
                  : "Usa el botón + para subir tu primer archivo"}
            </p>
          </div>
        ) : (
          <div className="file-grid">
            {filteredFiles.map((file, index) => (
              <FileCard
                key={`${file.r2Key || file.fileName}-${index}`}
                file={file}
                onView={handleViewFile}
                onDelete={mode === "shared" ? undefined : handleDeleteFile}
                onShare={mode === "shared" ? undefined : handleShareFile}
                owner={mode === "shared" ? file.owner : undefined}
                isShared={mode === "shared"}
              />
            ))}
          </div>
        )}
      </div>

      {/* Solo mostrar el botón de upload en "mis archivos" */}
      {mode !== "shared" && <ButtonUpload onFileUploaded={fetchFiles} />}

      <FileViewer
        file={selectedFile}
        isOpen={isViewerOpen}
        onClose={() => {
          setIsViewerOpen(false)
          setSelectedFile(null)
        }}
      />
    </div>
  )
}